import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, ArrowRight, Calendar } from "lucide-react";
import Seo, { breadcrumbLd } from "@/components/seo/Seo";
import { GlowOrb } from "@/components/common/GlowOrb";
import { usePublishedCaseStudies } from "@/data/caseStudyRepo";
import { openEstimateWizard, buildConsultationHref } from "@/lib/cta";
import { trackClick } from "@/lib/analytics";

/**
 * /case-studies — evidence-focused discovery hub.
 * Deliberately distinct from /portfolio (broader gallery) — this page only
 * lists case studies where challenge, solution and implementation have been
 * documented.
 */
export default function CaseStudies() {
  const { items } = usePublishedCaseStudies();

  const breadcrumb = breadcrumbLd([
    { name: "Home", path: "/" },
    { name: "Case Studies", path: "/case-studies" },
  ]);

  const itemList = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.map((cs, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://www.cloudlabweb.com/case-studies/${cs.slug}`,
      name: `${cs.projectName} · Case Study`,
    })),
  }), [items]);

  return (
    <div className="relative min-h-screen pt-24 md:pt-32" data-testid="case-studies-hub">
      <Seo
        title="Case Studies · Evidence of what CloudLab has actually built"
        description="Detailed engineering case studies from CloudLab Technologies — the real problems, the systems we built, the technology stacks and the verified results."
        keywords="CloudLab case studies, software engineering evidence, AI CRM case study, EdTech case study, UniCRM.ai case study, TeachingSoftware.ai case study"
        type="website"
        jsonLd={[breadcrumb, itemList]}
      />
      <GlowOrb className="top-10 -left-20" color="primary" size={620} />
      <GlowOrb className="bottom-40 -right-20" color="secondary" size={620} />

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 pb-10">
        <nav aria-label="Breadcrumb" className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
          <Link to="/" className="hover:text-primary transition-colors">Home</Link>
          <span className="mx-2 opacity-50">/</span>
          <span className="text-foreground">Case Studies</span>
        </nav>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-primary"
        >
          <Sparkles className="w-3.5 h-3.5" /> Case Studies
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-4 font-heading font-black text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] max-w-4xl"
        >
          What CloudLab <span className="text-primary">actually built</span> — with the evidence.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12, duration: 0.7 }}
          className="mt-5 text-lg text-muted-foreground max-w-2xl leading-relaxed"
        >
          Evidence-led engineering write-ups: the operational problem, the system we built, the technology stack and the verified results — no fabricated
          metrics, no stock statistics.
        </motion.p>
      </section>

      {/* Case study grid */}
      <section className="relative max-w-7xl mx-auto px-6 py-8 md:py-12">
        {items.length === 0 ? (
          <div className="rounded-3xl border border-border bg-card p-10 text-center text-muted-foreground">
            No published case studies yet.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            {items.map((cs, i) => (
              <motion.article
                key={cs.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * i, duration: 0.45 }}
                data-testid={`case-study-card-${cs.slug}`}
              >
                <Link
                  to={`/case-studies/${cs.slug}`}
                  className="group block rounded-3xl overflow-hidden border border-border bg-card hover:border-primary/40 transition-all hover:-translate-y-0.5 hover:shadow-[0_35px_80px_-40px_hsl(var(--primary)/0.4)]"
                >
                  <div className="aspect-[16/9] overflow-hidden bg-muted/40">
                    {cs.heroImage && (
                      <img
                        src={cs.heroImage}
                        alt={cs.projectName}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    )}
                  </div>
                  <div className="p-6 md:p-7">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {cs.industry && (
                        <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-primary bg-primary/10 border border-primary/20 px-2 py-1 rounded-full">
                          {cs.industry}
                        </span>
                      )}
                      {cs.solutionType && (
                        <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground bg-foreground/5 border border-border px-2 py-1 rounded-full">
                          {cs.solutionType}
                        </span>
                      )}
                    </div>
                    <h2 className="font-heading font-bold text-2xl md:text-3xl leading-tight group-hover:text-primary transition-colors">
                      {cs.projectName}
                    </h2>
                    <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                      {cs.shortDescription}
                    </p>
                    {cs.services?.length > 0 && (
                      <div className="mt-5 flex flex-wrap gap-1.5">
                        {cs.services.slice(0, 3).map((s) => (
                          <span key={s} className="text-[10px] font-mono px-2 py-1 rounded-full bg-background border border-border">
                            {s.replace(/-/g, " ")}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary">
                      View case study <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </section>

      {/* Cross-link to Portfolio */}
      <section className="relative max-w-7xl mx-auto px-6 py-12 md:py-16">
        <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Looking for the wider gallery?</div>
          <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl leading-tight">
            The full CloudLab portfolio.
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Case studies are the deep engineering write-ups. Head to the portfolio for a broader gallery — web platforms, mobile apps, AI models and
            research pieces.
          </p>
          <div className="mt-5">
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-border hover:border-primary/50 hover:bg-primary/5 text-sm font-medium transition-colors"
              data-testid="case-studies-hub-portfolio-link"
            >
              Explore the portfolio <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative max-w-7xl mx-auto px-6 pb-20">
        <div className="rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/[0.08] to-secondary/[0.08] p-8 md:p-12 text-center">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Building something similar?</div>
          <h3 className="mt-3 font-heading font-black text-3xl md:text-4xl">Let&apos;s make you the next case study.</h3>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              onClick={() => {
                trackClick("cta_clicked", {
                  cta_type: "get_project_estimate",
                  source_type: "case_study_hub",
                  cta_location: "final",
                });
                openEstimateWizard({
                  sourceType: "case_study_hub",
                  ctaLocation: "final",
                });
              }}
              data-testid="case-studies-hub-final-cta"
              className="rounded-full px-6 py-3 bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center gap-2 shadow-[0_15px_40px_-15px_hsl(var(--primary)/0.6)]"
            >
              <span>Get Project Estimate</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to={buildConsultationHref({})}
              onClick={() =>
                trackClick("cta_clicked", {
                  cta_type: "book_consultation",
                  source_type: "case_study_hub",
                  cta_location: "final",
                })
              }
              data-testid="case-studies-hub-final-consultation"
              className="rounded-full px-6 py-3 border border-border text-sm inline-flex items-center gap-2 hover:bg-foreground/5"
            >
              <Calendar className="w-4 h-4" />
              Book a 15-Minute Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
